import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

const transaction = `{
  "txid": "a3b5c7d9e1f2g3h4i5j6k7l8m9n0o1p2q3r4s5t6u7v8w9x0y1z2a3b4c5d6e7f8",
  "version": 1,
  "locktime": 0,
  "vin": [
    {
      "txid": "b1c2d3e4f5g6h7i8j9k0l1m2n3o4p5q6r7s8t9u0v1w2x3y4z5a6b7c8d9e0f1",
      "vout": 0,
      "scriptSig": {
        "asm": "3045022100abcdef1234567890abcdef1234567890abcdef1234567890abcdef123456789022034567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef01 02abcdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef",
        "hex": "483045022100abcdef1234567890abcdef1234567890abcdef1234567890abcdef123456789022034567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef012102abcdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef"
      },
      "sequence": 4294967295
    }
  ],
  "vout": [
    {
      "value": 0.01500000,
      "n": 0,
      "scriptPubKey": {
        "asm": "OP_DUP OP_HASH160 76a91489abcdefabbaabbaabbaabbaabbaabbaabbaabba88ac",
        "hex": "76a91489abcdefabbaabbaabbaabbaabbaabbaabbaabba88ac",
        "reqSigs": 1,
        "type": "pubkeyhash",
        "addresses": [
          "1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
        ]
      }
    }
  ]
}
`;
function BitcoinTechPage73() {
  const contentRef = useRef(null);
  const titleText = " Understanding UTXO Model and Bitcoin's Transactions";
  const links = {
    prev: "/bitcoin-tech-page-72",
    index: "/index-bitcoin-tech",
    next: "/bitcoin-tech-page-74",
  };
  const questions = [
    {
      text: "1. How does Bitcoin track ownership and transactions?",
      options: [
        {
          value: "A",
          label: "A. By storing account balances on a central server",
        },
        {
          value: "B",
          label:
            "B. By using the UTXO model, where unspent outputs from previous transactions are used as inputs for new transactions",
        },
        {
          value: "C",
          label:
            "C. By assigning each user a unique serial number for tracking funds",
        },
        {
          value: "D",
          label:
            "D. By using a single ledger that records all balances without referencing past transactions",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What happens if a Bitcoin transaction has leftover value after sending the intended amount?",
      options: [
        {
          value: "A",
          label:
            "A. The remaining amount is automatically sent back to the original sender’s address",
        },
        {
          value: "B",
          label:
            "B. The remaining amount is added as a transaction fee for miners",
        },
        {
          value: "C",
          label:
            "C. The remaining amount is returned as a new UTXO controlled by the sender’s wallet",
        },
        {
          value: "D",
          label: "D. The remaining amount is lost forever",
        },
      ],
      correct: "C",
    },
    {
      text: "3. What is the role of miners in Bitcoin transactions?",
      options: [
        {
          value: "A",
          label:
            "A. Miners create new Bitcoin and distribute it to users randomly",
        },
        {
          value: "B",
          label:
            "B. Miners verify and group transactions into blocks, securing the network through proof-of-work",
        },
        {
          value: "C",
          label:
            "C. Miners act as central authorities who approve or reject transactions",
        },
        {
          value: "D",
          label:
            "D. Miners hold a list of all Bitcoin users and update their balances",
        },
      ],
      correct: "B",
    },
    {
      text: "4. Why is proper UTXO management important for Bitcoin users?",
      options: [
        {
          value: "A",
          label:
            "A. It helps in reducing transaction fees and improving efficiency",
        },
        {
          value: "B",
          label: "B. It prevents miners from blocking transactions",
        },
        {
          value: "C",
          label:
            "C. It allows users to store Bitcoin in a more centralized manner",
        },
        {
          value: "D",
          label: "D. It ensures that transactions can be reversed if needed",
        },
      ],
      correct: "A",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={73}
        timerMinutes={60}
        currentLevel={6}
        unlockedBadges={11}
      />

      <p className="content" ref={contentRef}>
        <b>The UTXO Model: A Beginner's Guide</b> <br />
        <br />
        Bitcoin does not keep track of balances like a bank. Instead, it uses a
        system called the UTXO (Unspent Transaction Output) model. <br />
        Here’s how it works:
        <br />
        <br />
        Every Bitcoin transaction consists of inputs (where the Bitcoin is
        coming from) and outputs (where the Bitcoin is going).
        <br />
        The Bitcoin you own is actually a collection of UTXOs that have been
        assigned to your Bitcoin address. You can think of UTXOs like individual
        bills or coins in your pocket.
        <br />
        When you spend Bitcoin, you are using one or more UTXOs as inputs to
        create a new transaction, which in turn generates new UTXOs for the
        recipient and any change that comes back to you.
        <br />
        UTXOs are stored on the blockchain and can only be spent by the owner
        who holds the corresponding private key.
        <br />
        <br />
        <b>Example of a UTXO-Based Transaction</b>
        <br />
        <br />
        Imagine you have two UTXOs:
        <br />
        <br />
        - 0.3 BTC
        <br />
        - 0.7 BTC
        <br />
        <br />
        Now, you want to send 0.5 BTC to a friend. Your wallet selects the 0.7
        BTC UTXO as input and creates the following outputs:
        <br />
        <br />
        - 0.5 BTC sent to your friend
        <br />
        - 0.2 BTC sent back to you as "change"
        <br />
        <br />
        Just like when you buy something with cash and receive change, Bitcoin
        transactions often include change outputs. The difference is that the
        leftover amount is sent to a new address controlled by your wallet
        rather than being deducted from a stored balance.
        <br />
        If you were to spend all your Bitcoin in a transaction, you would not
        receive change, as all inputs would be used entirely for the outputs.
        <br />
        Bitcoin is often described as a decentralized digital currency, but what
        really makes it work under the hood? One of the key components of
        Bitcoin is its transaction system, which operates differently from how
        traditional banks process payments. In this lesson, we will break down
        Bitcoin transactions and the UTXO (Unspent Transaction Output) model in
        a way that is easy to understand for beginners.
        <br />
        <br />
        <br />
        <b>UTXO Management</b>
        <br />
        <br />
        Managing UTXOs effectively is crucial for optimizing Bitcoin
        transactions. When you send Bitcoin, your transaction must reference
        specific UTXOs as inputs. The selection of UTXOs impacts transaction
        fees, privacy, and efficiency.
        <br />
        <br />
        <b>Key Aspects of UTXO Management</b>
        <ul>
          <li>
            Consolidating UTXOs can help reduce transaction costs in the long
            run.
          </li>
          <li>
            Reusing the same UTXOs repeatedly can make transactions easier to
            track and reduce privacy.
          </li>
          <li>
            Breaking large UTXOs into smaller ones and using CoinJoin or similar
            techniques can enhance privacy.
          </li>
          <li>
            When spending a UTXO, any remaining balance must be returned as a
            change output.
          </li>
          <li>
            Efficiently managing change outputs helps prevent unnecessary
            transaction bloat.
          </li>
          <li>
            Very small UTXOs, known as dust, may become uneconomical to spend
            due to high transaction fees.
          </li>
          <li>
            Regularly consolidating small UTXOs can prevent them from becoming
            unusable.
          </li>
          <li>
            Grouping multiple payments into a single transaction can reduce
            costs and improve efficiency.
          </li>
          <li>
            Most modern Bitcoin wallets have built-in algorithms that manage
            UTXOs efficiently, but understanding these principles helps users
            minimize fees and improve security.
          </li>
        </ul>
        <b>Why the UTXO Model Matters</b>
        <br />
        <br />
        The UTXO model is fundamental to Bitcoin’s security and efficiency. It
        ensures:
        <ul>
          <li>
            All Bitcoin transactions are publicly recorded, making it easy to
            verify ownership and transaction history.
          </li>
          <li>
            Digital signatures ensure that only the rightful owner of a UTXO can
            spend it.
          </li>
          <li>
            Transactions are processed in a way that prevents double-spending
            and preserves Bitcoin’s decentralized nature.
          </li>
          <li>
            The UTXO model improves scalability by allowing wallets and nodes to
            track only unspent outputs instead of continuously updated balances.
          </li>
          <li>
            Although Bitcoin is not fully anonymous, the UTXO model enables
            users to structure transactions in ways that make spending patterns
            harder to track.
          </li>
        </ul>
        <br />
        <b>Understanding the Anatomy of a Bitcoin Transaction</b>
        <br />
        <br />
        Bitcoin transactions are the lifeblood of the Bitcoin network. Every
        time a user sends or receives Bitcoin, a transaction is created,
        validated by the network, and recorded on the blockchain. But what
        actually happens under the hood? Here we will mdemystify the structure
        of a Bitcoin transaction, breaking it down into its essential
        components.
        <br />
        Understanding Bitcoin transactions is crucial for developers,
        researchers, and Bitcoin enthusiasts. Whether you're building Bitcoin
        applications, analyzing on-chain data, or simply curious about how
        Bitcoin transfers value in a trustless system, this page will serve as
        your comprehensive guide. <br />
        Through interactive examples, real-world transactions, and step-by-step
        dissections in the forth-coming curriculum , you’ll gain hands-on
        knowledge of how Bitcoin transactions work, helping you transition from
        a casual user to an informed Bitcoin participant. <br />
        <br />
        Ready to dive into the world of Bitcoin transactions? Let's begin!
        <br />
        <br />
        A Bitcoin transaction is a transfer of value from one person to another
        using the Bitcoin network. Unlike traditional banking systems where
        balances are stored as numbers in an account, Bitcoin uses a different
        method: every transaction references past transactions as its inputs and
        creates new outputs that can be spent in future transactions.
        <br />
        Think of Bitcoin as a system where people pass around digital "notes"
        rather than updating a bank balance. When you send Bitcoin, you are
        effectively signing over the ownership of existing "notes" to someone
        else, and the network verifies this before completing the transaction.
        <br />
        Each transaction is recorded on Bitcoin's blockchain, ensuring
        transparency and security. Transactions are irreversible, meaning once
        Bitcoin is sent, it cannot be undone unless the recipient chooses to
        send it back. This finality is one of Bitcoin’s strongest features
        compared to traditional banking, where transactions can be reversed by
        banks or payment processors.
        <br />
        <br />
        <b>Inputs, Outputs, and Digital Signatures</b>
        <br />
        <br />
        Each input in a Bitcoin transaction contains a reference to a previous
        UTXO and a digital signature proving that the sender has the right to
        spend it. This digital signature is generated using the sender’s private
        key and ensures that transactions cannot be altered or forged. <br />
        The outputs specify where the Bitcoin should go and how much should be
        assigned to each recipient.
        <br />
        To prevent fraudulent transactions, Bitcoin nodes verify each
        transaction by checking:
        <br />
        - That the referenced UTXOs are valid and have not been spent before
        (preventing double-spending).
        <br />
        - That the digital signature is correct and matches the owner of the
        UTXOs.
        <br />
        <br />
        Here’s a dummy Bitcoin transaction in JSON format with one input, one
        output, and a Pay-to-PubKey-Hash (P2PKH) script:
      </p>
      <div className="code-block" style={{ textAlign: "left" }}>
        <pre>{transaction}</pre>
      </div>
      <p className="content">
        Here is a brief explanation of the fields in the above Bitcoin
        Transaction, more of which we will cover in detail in the forth-coming
        days. <br />
        <ul>
          <li>txid is the unique identifier for the transaction.</li>
          <li>
            version indicates the transaction version, which is 1 in this case.
          </li>
          <li>
            locktime specifies the time or block height at which the transaction
            can be included in a block, where 0 means it can be included
            immediately.
          </li>
          <li>vin represents the input section of the transaction.</li>
          <li>
            txid in vin refers to the transaction ID of the previous transaction
            being spent.
          </li>
          <li>
            vout specifies the index of the output being spent from the previous
            transaction.
          </li>
          <li>
            scriptSig is the unlocking script containing the signature and
            public key that proves ownership of the funds.
          </li>
          <li>
            asm provides a human-readable representation of the unlocking
            script.
          </li>
          <li>hex is the hex-encoded version of the unlocking script.</li>
          <li>
            sequence is set to 4294967295, indicating that the transaction is
            not using relative timelocks.
          </li>
          <li>vout represents the output section of the transaction.</li>
          <li>
            value indicates the amount in BTC, which is 0.015 BTC in this case.
          </li>
          <li>n is the output index, which is 0 in this case.</li>
          <li>
            scriptPubKey is the locking script that specifies the conditions
            required to spend the output.
          </li>
          <li>
            asm provides a human-readable format of the locking script using
            standard Bitcoin opcodes.
          </li>
          <li>hex is the hex-encoded version of the scriptPubKey.</li>
          <li>
            reqSigs specifies the number of required signatures, which is 1 for
            a P2PKH output.
          </li>
          <li>
            type identifies the output type as pubkeyhash for a P2PKH
            transaction.
          </li>
          <li>
            addresses lists the recipient Bitcoin addresses for the output.
          </li>
        </ul>
        <b>Common Misconceptions About Bitcoin Transactions</b>
        <br />
        <br />
        1. Bitcoin transactions are anonymous - While Bitcoin transactions do
        not include personal information, they are publicly recorded on the
        blockchain. Anyone can trace transactions using blockchain explorers,
        though various privacy techniques (e.g., CoinJoin) can make tracking
        harder.
        <br />
        2. Bitcoin transactions are free - Transactions require a small fee to
        incentivize miners to process them. Fees fluctuate based on network
        demand.
        <br />
        3. You can send Bitcoin without an internet connection - In normal
        cases, you need an internet connection to broadcast transactions, but
        alternative methods like radio signals and satellites are being
        explored.
        <br />
        <br />
        Thus far we have learned how Bitcoin transactions consist of inputs and
        outputs, with the UTXO model tracking unspent Bitcoin rather than
        account balances. When Bitcoin is sent, old UTXOs are spent, and new
        ones are created. Digital signatures provide security, ensuring
        transactions cannot be altered, while Bitcoin nodes verify transactions
        to prevent fraud and double-spending. Miners confirm transactions and
        add them to the blockchain, ensuring the integrity of the network.
        Transaction fees influence how quickly a transaction is confirmed, as
        higher fees incentivize miners to prioritize processing. Proper UTXO
        management enhances efficiency, reduces costs, and improves privacy,
        making it an essential aspect of Bitcoin transactions.
        <br />
        Understanding how transactions and the UTXO model work is essential to
        grasping Bitcoin’s power as a decentralized digital currency. In the
        next lesson, we will explore how these transactions are confirmed and
        recorded on the blockchain, as well as the role of miners in securing
        the network.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default BitcoinTechPage73;
